import React, { useState } from "react";
import Icon from "../../../components/AppIcon";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";

const BulkActionsBar = ({
  selectedCount,
  onSubmitAssignments,
  onClearSelection,
  regions = [],
  clusters = [],
  teamMembers = [],
  userRole,
  isSubmitting = false,
}) => {
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedCluster, setSelectedCluster] = useState("");
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [touchedMember, setTouchedMember] = useState(false);
  const [touchedStatus, setTouchedStatus] = useState(false);

  const regionOptions = [
    ...(regions?.map((region) => ({
      value: region.id,
      label: region.name,
    })) || []),
  ];

  const clusterOptions = [
    ...(clusters?.map((cluster) => ({
      value: cluster.id,
      label: cluster.name,
    })) || []),
  ];

  const memberOptions = [
    { value: "", label: "— No Member —" },
    ...(teamMembers?.map((member) => ({
      value: member.id,
      label: `${member.unique_id || ""}${member.unique_id ? " - " : ""}${member.name || "Unnamed"} (${member.teams?.name ?? "—"})`,
    })) || []),
  ];

  const statusOptions = [
    { value: "survey_not_started", label: "Survey Not Started" },
    { value: "survey_inprogress", label: "Survey In Progress" },
    { value: "survey_unworkable", label: "Survey Unworkable" },
    { value: "survey_complete", label: "Survey Complete" },
    { value: "transport_pending", label: "Transport Pending" },
    { value: "transport_inprogress", label: "Transport In Progress" },
    { value: "transport_complete", label: "Transport Complete" },
    { value: "civil_pending", label: "Civil Pending" },
    { value: "civil_inprogress", label: "Civil In Progress" },
    { value: "civil_complete", label: "Civil Complete" },
    { value: "technical_pending", label: "Technical Pending" },
    { value: "technical_inprogress", label: "Technical In Progress" },
    { value: "technical_complete", label: "Technical Complete" },
    {
      value: "commissioning_not_started",
      label: "Commissioning Not Started",
    },
    {
      value: "commissioning_inprogress",
      label: "Commissioning In Progress",
    },
    { value: "commissioning_complete", label: "Commissioning Complete" },
  ];

  const isAdmin = userRole === "super_admin" || userRole === "project_admin";

  const hasAnySelection =
    selectedRegion ||
    selectedCluster ||
    touchedMember ||
    touchedStatus;

  const handleSubmit = () => {
    if (!hasAnySelection || !onSubmitAssignments) return;
    const payload = {
      regionId: selectedRegion || undefined,
      clusterId: selectedCluster || undefined,
      memberId: touchedMember
        ? selectedMember === ""
          ? null
          : selectedMember || undefined
        : undefined,
      status: touchedStatus
        ? selectedStatus === ""
          ? null
          : selectedStatus || undefined
        : undefined,
    };
    onSubmitAssignments(payload);
    setSelectedRegion("");
    setSelectedCluster("");
    setSelectedMember("");
    setSelectedStatus("");
    setTouchedMember(false);
    setTouchedStatus(false);
  };

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[1500] w-full max-w-4xl px-4">
      <div className="bg-card border-2 border-primary rounded-lg shadow-2xl p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Selection Info */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="CheckSquare" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">
                {selectedCount} customer{selectedCount > 1 ? "s" : ""} selected
              </p>
              <button
                onClick={onClearSelection}
                className="text-xs text-primary hover:underline"
              >
                Clear selection
              </button>
            </div>
          </div>

          {/* Bulk assign: 2x2 grid + Apply */}
          <div className="flex items-end gap-3 w-full sm:w-auto">
            <div className="grid grid-cols-2 gap-2 w-full min-w-[420px] sm:w-auto">
              {isAdmin && (
                <>
                  <Select
                    options={regionOptions}
                    value={selectedRegion}
                    onChange={setSelectedRegion}
                    placeholder="Region"
                    className="min-w-[200px] w-full"
                    searchable
                  />
                  <Select
                    options={clusterOptions}
                    value={selectedCluster}
                    onChange={setSelectedCluster}
                    placeholder="Cluster"
                    className="min-w-[200px] w-full"
                    searchable
                  />
                </>
              )}
              <Select
                options={statusOptions}
                value={selectedStatus}
                onChange={(v) => {
                  setTouchedStatus(true);
                  setSelectedStatus(v);
                }}
                placeholder="Status"
                className="min-w-[200px] w-full"
                searchable
              />
              <Select
                options={memberOptions}
                value={selectedMember}
                onChange={(v) => {
                  setTouchedMember(true);
                  setSelectedMember(v);
                }}
                placeholder="Team Member"
                className="min-w-[200px] w-full"
                searchable
              />
            </div>
            <Button
              variant="default"
              size="sm"
              iconName={isSubmitting ? "Loader2" : "Check"}
              iconPosition="left"
              iconClassName={isSubmitting ? "animate-spin" : ""}
              onClick={handleSubmit}
              disabled={!hasAnySelection || isSubmitting}
              className="shrink-0"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkActionsBar;
